import axios, {AxiosInstance, Method} from 'axios';
import {v4} from 'uuid';

const APIInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL,
    headers: {'content-type': 'application/json', accept: 'application/json'},
});

export default class AnalyticsAPI {
    public static async LogEvent(event: string, data: any, options?: {useSqsLogging?: boolean}): Promise<void> {
        const payload = {
            ...data,
            sendingApp: 'CUSTOMER_PORTAL',
            $insert_id: v4(),
            time: Date.now(),
        };

        // TODO: the walkthrough path segment is used to determine the
        // project/key to use but all analytics for these properties should be
        // under the same project ie walkthrough.
        const loggerUrl = options?.useSqsLogging ? `/v2/log/walkthrough/${event}/sqs` : `/v2/log/walkthrough/${event}`;

        return this.doCall(APIInstance, loggerUrl, 'POST', payload);
    }

    /**
     * A pre-consent funnel event.
     *
     * The payload is built from scratch rather than filtered down from the identified one: an
     * allowlist cannot leak a field somebody adds later, a denylist can. Only the event name
     * travels — no answers, no device details, no user id or email, and no IP, which athena
     * withholds when it sees consent_state 'pending'.
     */
    public static async LogAnonymousEvent(
        event: string,
        distinctId: string,
        abflags: Record<string, boolean | string>,
    ): Promise<{status: boolean}> {
        // Same field, same meaning, as the identified path: which deploy the visitor was on.
        // It describes the build, not the person, so it carries nothing about them.
        const payload = {
            action: event,
            distinct_id: distinctId,
            sendingApp: 'CUSTOMER_PORTAL',
            $insert_id: v4(),
            time: Date.now(),
            // Marks the cohort so these are analysed separately rather than blended with
            // identified sessions, where they would double-count anyone who consents midway.
            consent_state: 'pending',
            // Which arm the visitor was served. Without it this cohort shows conversion but
            // cannot attribute it, so experiments are unmeasurable for everyone who has not
            // answered the banner. Assignment is random and says nothing about the person.
            abflags,
            version: process.env.NEXT_PUBLIC_STAGE,
        };

        return this.doCall(APIInstance, `/v2/log/walkthrough/${event}/sqs`, 'POST', payload);
    }

    private static async doCall(
        Instance: AxiosInstance,
        url: string,
        method: Method,
        data?: any,
        params?: any,
    ): Promise<any> {
        const config = data ? {method, url, data} : {method, url, params};
        try {
            const response = await Instance.request(config);

            return {status: true, data: response.data};
        } catch (e) {
            return {status: false};
        }
    }
}
